import { ExceptionEvent } from "@fairfleet/geotab";

/** Just the API call this needs, so it can be tested without a Geotab client. */
export type GeotabCall = (method: string, params: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown>;

export interface FetchExceptionEventsOptions {
  /** ExceptionEventSearch without the dates, e.g. `{ ruleSearch: { id } }` or `{ deviceSearch: { groups } }`. */
  search: Record<string, unknown>;
  from: Date;
  to: Date;
  /** `resultsLimit` per request; a page this full means there may be more. */
  pageSize: number;
  maxPages: number;
  /** Filter applied while paging, so other rules' events of a vehicle stream are not held in memory. */
  keep?: (event: ExceptionEvent) => boolean;
  onPage?: () => void;
  signal?: AbortSignal;
}

export interface FetchExceptionEventsResult {
  items: ExceptionEvent[];
  /** False when `maxPages` was reached or paging stopped advancing – rows may be missing then. */
  complete: boolean;
  requests: number;
  /** Events Geotab sent, kept or not. */
  read: number;
}

/**
 * Every event matching `search` in [from, to], paged with `Get`'s `sort` (date ascending, continuing after the
 * last row's `activeFrom` + `id`) – verified live on ExceptionEvent (fairfleet 2026-11-04): pages in time order,
 * gap-free, no overlap. A row seen twice is still dropped by id.
 */
export async function fetchExceptionEvents(
  call: GeotabCall,
  { search, from, to, pageSize, maxPages, keep, onPage, signal }: FetchExceptionEventsOptions,
): Promise<FetchExceptionEventsResult> {
  const byId = new Map<string, ExceptionEvent>();
  let cursor: { offset: string; lastId: string } | undefined;
  let requests = 0;
  let read = 0;
  const result = (complete: boolean) => ({ items: [...byId.values()], complete, requests, read });

  for (let page = 0; page < maxPages; page++) {
    signal?.throwIfAborted();
    const rows = await call(
      "Get",
      {
        typeName: "ExceptionEvent",
        search: { ...search, fromDate: from.toISOString(), toDate: to.toISOString() },
        resultsLimit: pageSize,
        sort: { sortBy: "date", sortDirection: "asc", ...cursor },
      },
      signal,
    );
    requests++;
    onPage?.();
    if (!Array.isArray(rows)) {
      throw new Error(`Unexpected ExceptionEvent response: expected an array, got ${String(rows)}`);
    }
    read += rows.length;
    for (const row of rows as ExceptionEvent[]) {
      if (!keep || keep(row)) byId.set(row.id, row);
    }

    if (rows.length < pageSize) return result(true);
    const last = rows[rows.length - 1] as ExceptionEvent;
    if (!last?.id || !last.activeFrom) return result(false);
    const next = { offset: String(last.activeFrom), lastId: last.id };
    if (cursor && next.offset === cursor.offset && next.lastId === cursor.lastId) return result(false);
    cursor = next;
  }
  return result(false);
}
